// DashboardActionCards — Feb 2026 redesign.
//
// Vervangt zowel `ActiveChallengeCTA` (de gele samenvattingsbalk) als
// `QuickActions` (3 kleine vierkante tegels) door 3 grote horizontale
// action-cards op het dashboard: Challenges / Meet-up / Chats.
//
// Elk kaartje toont:
//   • gekleurde icon-ring (goud / groen / blauw)
//   • titel + subtitle
//   • gekleurd count-label + groot count-getal
//   • chevron rechts als affordance
//
// Data-fetches gebeuren in één `Promise.all` zodat de kaart in één
// render-pass live counts kan tonen zonder extra layout-shift.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Swords, MapPin, MessageCircle, ChevronRight } from "lucide-react";
import api from "@/api";

/**
 * Grote horizontale action-card. Volledig tap-target.
 *
 * @param {object}   props
 * @param {Function} props.icon      lucide icon component
 * @param {string}   props.color     hex accent (icon + count label)
 * @param {string}   props.title     grote titel (bijv. "Challenges")
 * @param {string}   props.subtitle  kleiner grijs bijzin (bijv. "Doe mee")
 * @param {string}   props.countLbl  gekleurd label boven het getal
 * @param {number|null} props.count  getal onder de labels — null → skeleton
 * @param {string}   props.to        route om naar te navigeren
 * @param {string}   props.testId    data-testid op de knop
 */
function ActionCard({ icon: Icon, color, title, subtitle, countLbl, count, to, testId, badge }) {
    const nav = useNavigate();
    return (
        <button
            type="button"
            onClick={() => nav(to)}
            data-testid={testId}
            className="w-full text-left rounded-2xl p-4 active:scale-[0.99] transition
                       flex items-center gap-4 relative overflow-hidden"
            style={{
                background: `linear-gradient(135deg, ${color}12 0%, ${color}03 60%, transparent 100%)`,
                border: `1px solid ${color}38`,
                boxShadow: `0 0 24px ${color}0F`,
            }}
        >
            {/* Icon in gekleurde ring */}
            <div
                className="h-14 w-14 rounded-full flex items-center justify-center shrink-0"
                style={{
                    background: `${color}14`,
                    border: `1px solid ${color}55`,
                    boxShadow: `0 0 20px ${color}25`,
                }}
            >
                <Icon size={22} style={{ color }} strokeWidth={2.1} />
            </div>

            {/* Titel + subtitle + count */}
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-display text-[17px] font-bold leading-tight">
                        {title}
                    </p>
                    {badge && (
                        <span
                            data-testid={`${testId}-badge`}
                            className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full"
                            style={{
                                background: "#FFD60A",
                                color: "#0a0a0c",
                                boxShadow: "0 0 12px rgba(255,214,10,0.55)",
                            }}
                        >
                            {badge}
                        </span>
                    )}
                </div>
                <p className="text-[12px] text-muted-foreground mt-0.5">
                    {subtitle}
                </p>
                <p
                    className="text-[11px] font-semibold mt-1"
                    style={{ color }}
                >
                    {countLbl}
                </p>
                <p
                    className="font-display text-[22px] font-bold tabular-nums leading-none mt-0.5"
                    data-testid={`${testId}-count`}
                >
                    {count === null ? "—" : count}
                </p>
            </div>

            {/* Chevron */}
            <ChevronRight size={22} style={{ color }} className="shrink-0" />
        </button>
    );
}

export default function DashboardActionCards() {
    const { t } = useTranslation();
    const [counts, setCounts] = useState({
        challenges: null,
        meetups: null,
        unread: null,
        // Feb 2026 IA reshuffle: nu de Challenges-tab uit de bottom-nav
        // is verdwenen, moet de dashboard-card duidelijk signaleren als
        // er iets op je actie wacht.  We tellen challenge-invites én
        // meetup-RSVPs die de user nog niet beantwoord heeft.
        pendingChallenges: 0,
        pendingMeetups: 0,
    });

    useEffect(() => {
        let alive = true;
        (async () => {
            const [me, list, twin, meetups, rooms] = await Promise.all([
                api.get("/auth/me").then((r) => r.data).catch(() => null),
                api.get("/challenges/list")
                    .then((r) => r.data?.items || r.data || [])
                    .catch(() => []),
                api.get("/twin/current").then((r) => r.data).catch(() => null),
                api.get("/meetups/list").then((r) => r.data?.items || []).catch(() => []),
                api.get("/chats/rooms").then((r) => r.data?.items || r.data || []).catch(() => []),
            ]);
            if (!alive) return;

            const activeChallenges = list.filter((c) => c.status === "active").length
                + (twin && (twin.status === "open" || twin.status === "active") ? 1 : 0);

            const activeMeetups = meetups.filter(
                (m) => (m.status || "active") === "active"
            ).length;

            const unread = rooms.reduce(
                (acc, r) => acc + (Number(r.unread_count) || 0), 0
            );

            // Pending = expliciet nog niet geaccepteerd/beantwoord.
            const meId = me?.id;
            const pendingChallenges = meId
                ? list.filter((c) => {
                    const meP = (c.participants || []).find((p) => p.user_id === meId);
                    return meP?.status === "pending" && c.status !== "finished" && c.status !== "cancelled";
                }).length
                : 0;
            const pendingMeetups = meId
                ? meetups.filter((m) => {
                    if ((m.status || "active") !== "active") return false;
                    if (m.created_by?.user_id === meId) return false;
                    const my = (m.rsvps || {})[meId];
                    return !my || my.status === "maybe" || !my.status;
                }).length
                : 0;

            setCounts({
                challenges: activeChallenges,
                meetups: activeMeetups,
                unread,
                pendingChallenges,
                pendingMeetups,
            });
        })();
        return () => { alive = false; };
    }, []);

    return (
        <div className="px-4 mt-4 space-y-3" data-testid="dashboard-action-cards">
            <ActionCard
                icon={Swords}
                color="#FFD60A"
                title={t("dashboardActions.challenges_title")}
                subtitle={t("dashboardActions.challenges_sub")}
                countLbl={t("dashboardActions.challenges_count_label")}
                count={counts.challenges}
                to="/challenges"
                testId="dash-card-challenges"
                badge={counts.pendingChallenges > 0
                    ? t("dashboardActions.pending_badge", {
                          count: counts.pendingChallenges,
                          defaultValue: `${counts.pendingChallenges} wacht${counts.pendingChallenges === 1 ? '' : 'en'} op jou`
                      })
                    : null}
            />
            <ActionCard
                icon={MapPin}
                color="#32D74B"
                title={t("dashboardActions.meetup_title")}
                subtitle={t("dashboardActions.meetup_sub")}
                countLbl={t("dashboardActions.meetup_count_label")}
                count={counts.meetups}
                to="/plan"
                testId="dash-card-meetup"
                badge={counts.pendingMeetups > 0
                    ? t("dashboardActions.pending_rsvp_badge", {
                          count: counts.pendingMeetups,
                          defaultValue: `${counts.pendingMeetups} RSVP open`
                      })
                    : null}
            />
            <ActionCard
                icon={MessageCircle}
                color="#0A84FF"
                title={t("dashboardActions.chats_title")}
                subtitle={t("dashboardActions.chats_sub")}
                countLbl={t("dashboardActions.chats_count_label")}
                count={counts.unread}
                to="/chats"
                testId="dash-card-chats"
            />
        </div>
    );
}
